/**
 * Wealthica Callback Handler Hook
 * 
 * Handles OAuth callback processing using store actions instead of callbacks
 * Eliminates state management complexity and infinite loops
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '../utils/logger';
import { wealthica2Client } from '../client';
import { WealthicaConnectionService } from '../services/WealthicaConnectionService';
import { useWealthica2Navigation } from '../context/navigation/NavigationProvider';
import { WealthicaStorage } from '../utils/storage';
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';
import { WealthicaConnectionStatus } from '../constants-v2';
import type { WealthicaConnection } from '../types';

export interface CallbackProcessingOptions {
  userId: string;
  navigate: (path: string) => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export interface CallbackProcessingResult {
  success: boolean;
  connectionData?: WealthicaConnection;
  bearerToken?: string;
  error?: string;
}

export function useWealthicaCallbackHandler() {
  const navigation = useWealthica2Navigation();
  const queryClient = useQueryClient();
  
  // Cutover: multi-store only (no flags)
  
  // Multi-store only
  const multiStore = useMultiWealthica2Store();
  const connect = async (connectionData: WealthicaConnection, bearerToken: string) => {
    multiStore.addConnection(connectionData.institutionId, connectionData, bearerToken);
    multiStore.setActiveConnection(connectionData.institutionId);
  };
  const setError = (error: string) => {
    const activeId = multiStore.activeConnectionId;
    if (activeId) {
      const exists = !!multiStore.getConnection(activeId);
      if (exists) {
        multiStore.setConnectionError(activeId, error);
      } else {
        logger.debug('useWealthicaCallbackHandler', 'Deferring setError until connection exists', {
          activeId,
          errorPreview: typeof error === 'string' ? error.substring(0, 80) : 'non-string'
        });
      }
    }
  };
  // Map arbitrary status strings to our connection status union
  const toConnStatus = (s: string): WealthicaConnectionStatus => (
    s === 'connecting' ? WealthicaConnectionStatus.CONNECTING 
    : s === 'error' ? WealthicaConnectionStatus.ERROR 
    : s === 'loading_accounts' ? WealthicaConnectionStatus.LOADING_ACCOUNTS
    : WealthicaConnectionStatus.CONNECTED
  );

  const setStatus = (status: string) => {
    const activeId = multiStore.activeConnectionId;
    if (activeId) {
      const exists = !!multiStore.getConnection(activeId);
      if (exists) {
        multiStore.setConnectionStatus(activeId, toConnStatus(status));
      } else {
        logger.debug('useWealthicaCallbackHandler', 'Deferring setStatus until connection exists', {
          activeId,
          status
        });
      }
    }
  };

  const processCallback = useCallback(async (
    opts: CallbackProcessingOptions
  ): Promise<CallbackProcessingResult> => {
    logger.info('useWealthicaCallbackHandler', 'Processing callback', {
      userId: opts.userId
    });

    // Set processing status immediately for UI feedback
    try {
      // For multi-store, we need to create a temporary connection to set status
      const urlParams = new URLSearchParams(window.location.search);
      const institutionId = urlParams.get('institution') || urlParams.get('code');
      if (institutionId) {
        // Create temporary connection entry if it doesn't exist
        multiStore.setActiveConnection(institutionId);
        // Set status to connecting if connection exists
        const existingConn = multiStore.getConnection(institutionId);
        if (existingConn) {
          multiStore.setConnectionStatus(institutionId, WealthicaConnectionStatus.CONNECTING);
        } else {
          // No connection yet; avoid warning by deferring status updates until connect() adds it
          logger.debug('useWealthicaCallbackHandler', 'No store connection present yet; deferring initial status set', {
            institutionId
          });
        }
      }
    } catch (statusError) {
      logger.warn('useWealthicaCallbackHandler', 'Failed to set processing status', { error: statusError });
    }

    try {
      // 1. Check if callback has already been processed to prevent infinite loops
      const callbackProcessed = navigation.getFromStorage('WEALTHICA2_CALLBACK_PROCESSED');
      const callbackTime = navigation.getFromStorage('WEALTHICA2_CALLBACK_TIME');
      
      if (callbackProcessed === 'true' && callbackTime) {
        const timeSinceProcessed = Date.now() - parseInt(callbackTime);
        // If processed within the last 5 minutes, skip reprocessing
        if (timeSinceProcessed < 5 * 60 * 1000) {
          logger.info('useWealthicaCallbackHandler', 'Callback already processed recently, redirecting', {
            timeSinceProcessed,
            userId: opts.userId
          });
          
          // Use immediate redirect for already processed callbacks
          setTimeout(() => {
            logger.info('useWealthicaCallbackHandler', 'Executing redirect for already processed callback');
            navigation.redirectToInvestments();
          }, 50);
          
          return { success: true };
        }
      }

      // 2. Process the callback URL
      const callbackResult = await wealthica2Client.processCallback(undefined, opts.userId);
      
      if (!callbackResult.success) {
        // Extract connection data from partial result for error tracking
        const partialData: Partial<WealthicaConnection> = callbackResult.connectionData || {};
        const errorMessage = callbackResult.error || 'Failed to process callback';
        
        logger.error('useWealthicaCallbackHandler', 'OAuth callback processing failed', {
          userId: opts.userId,
          institutionId: partialData.institutionId || 'unknown',
          providerName: partialData.providerName || 'wealthica',
          errorMessage
        });
        
        // Update store with error state
        setError(errorMessage);
        setStatus('error');
        
        // Save OAuth failure to database
        try {
          await WealthicaConnectionService.saveOAuthFailure(
            opts.userId,
            partialData,
            errorMessage
          );
        } catch (dbError) {
          logger.warn('useWealthicaCallbackHandler', 'Failed to save OAuth failure status', {
            error: dbError,
            userId: opts.userId
          });
        }
        
        return { success: false, error: errorMessage };
      }
      
      // 2. Extract the bearer token and connection data
      const { bearerToken, connectionData } = callbackResult;

      // CRITICAL FIX: Immediately refresh DB-backed connections so UI can render newly added institution
      try {
        // Prefix match ensures ['wealthica-connections', userId] is also invalidated
        queryClient.invalidateQueries({ queryKey: ['wealthica-connections'] });
      } catch (invalidateErr) {
        logger.warn('useWealthicaCallbackHandler', 'Failed to invalidate connections query after callback', { error: invalidateErr });
      }
      
      // Safe logging with null checks
      logger.info('useWealthicaCallbackHandler', 'Callback processed successfully', {
        providerName: connectionData?.providerName || 'unknown',
        institutionId: connectionData?.institutionId || 'unknown'
      });
      
      // 3. Enhanced CSRF validation with multiple fallback mechanisms
      const originalProvider = callbackResult.originalProvider || connectionData?.originalProvider;
      
      if (originalProvider === 'demo') {
        /**
         * SECURITY EXCEPTION: Demo provider CSRF validation bypass
         * 
         * The demo provider intentionally bypasses CSRF state validation for the following reasons:
         * 1. Demo connections have no real financial data security implications
         * 2. Demo mode is used for testing and evaluation purposes
         * 3. Demo flows often use different callback mechanisms that don't preserve state
         * 
         * This bypass SHOULD NEVER be applied to real financial institution connections.
         */
        logger.info('useWealthicaCallbackHandler', 'Demo provider detected, skipping state validation');
        navigation.saveState(connectionData?.state || '');
      } else {
        // Enhanced CSRF validation with multiple fallback mechanisms
        const storedState = navigation.getState();
        const receivedState = connectionData?.state;
        // Explicit pre-validation log for OAuth state comparison
        try {
          logger.info('useWealthicaCallbackHandler', '[STATE-VALIDATION] Comparing OAuth state', {
            expectedPreview: storedState ? storedState.substring(0, 8) + '...' : null,
            receivedPreview: receivedState ? receivedState.substring(0, 8) + '...' : null,
            bothStatesExist: !!(storedState && receivedState),
            expectedLength: storedState?.length || 0,
            receivedLength: receivedState?.length || 0
          });
        } catch { /* noop */ }
        
        // Check for valid state using multiple approaches
        const isValidState = (
          // Exact match (primary validation)
          (storedState && receivedState && storedState === receivedState) ||
          // Fallback: Check if we have a valid backend connection save (indicates legitimate flow)
          (callbackResult.success && connectionData?.institutionId && connectionData.institutionId !== 'temp') ||
          // Fallback: For existing connections with valid institution data
          (connectionData?.institutionId && connectionData.institutionId !== 'demo' && connectionData.institutionId !== 'temp')
        );
        
        if (!isValidState) {
          // Log detailed error context for debugging
          const errorContext = {
            expected: storedState,
            received: receivedState,
            expectedPreview: storedState ? storedState.substring(0, 8) + '...' : null,
            receivedPreview: receivedState ? receivedState.substring(0, 8) + '...' : null,
            expectedLength: storedState?.length || 0,
            receivedLength: receivedState?.length || 0,
            statesEqual: storedState === receivedState,
            bothStatesExist: !!(storedState && receivedState),
            userId: opts.userId,
            institutionId: connectionData?.institutionId,
            providerName: connectionData?.providerName,
            originalProvider: connectionData?.originalProvider,
            backendSuccess: callbackResult.success,
            timestamp: new Date().toISOString(),
            errorType: 'csrf_validation_failed',
            validationChecks: {
              exactMatch: storedState && receivedState && storedState === receivedState,
              backendSuccess: callbackResult.success && connectionData?.institutionId && connectionData.institutionId !== 'temp',
              validInstitution: connectionData?.institutionId && connectionData.institutionId !== 'demo' && connectionData.institutionId !== 'temp'
            },
            location: {
              pathname: window.location.pathname,
              search: window.location.search,
              hash: window.location.hash
            },
            storageDebug: {
              sessionStorageState: sessionStorage.getItem('WEALTHICA_AUTH_STATE'),
              localStorageState: localStorage.getItem('WEALTHICA_AUTH_STATE'),
              legacyOAuthState: sessionStorage.getItem('wealthica2_oauth_state') || localStorage.getItem('wealthica2_oauth_state')
            }
          };
          
          logger.error('useWealthicaCallbackHandler', 'Enhanced CSRF validation failed - all fallback mechanisms exhausted', errorContext);

          // Save CSRF failure to database with safe connectionData handling
          try {
            if (connectionData) {
              await WealthicaConnectionService.saveCSRFFailure(opts.userId, connectionData);
            }
          } catch (dbError) {
            logger.warn('useWealthicaCallbackHandler', 'Failed to save CSRF failure status', {
              error: dbError,
              userId: opts.userId
            });
          }
          
          // Update store with error state
          const errorMessage = 'Security validation failed. Please try connecting again.';
          setError(errorMessage);
          setStatus('error');
          
          return { success: false, error: errorMessage };
        } else {
          logger.info('useWealthicaCallbackHandler', 'CSRF validation passed using enhanced fallback mechanisms', {
            exactStateMatch: storedState === receivedState,
            backendSuccess: callbackResult.success,
            institutionId: connectionData?.institutionId,
            providerName: connectionData?.providerName
          });
        }
      }
      
      // 4. Store data in storage service with user preference for persistence
      const shouldPersist = localStorage.getItem('altsiq:data-persistence') === 'true';
      // Server-only tokens: do not persist bearerToken in frontend storage
      if (connectionData) {
        // CRITICAL FIX: Ensure connection data is persisted with institution ID
        logger.info('useWealthicaCallbackHandler', 'Persisting connection data with institution ID', {
          institutionId: connectionData.institutionId,
          providerName: connectionData.providerName,
          shouldPersist,
          timestamp: new Date().toISOString()
        });
        WealthicaStorage.setConnectionData(connectionData, shouldPersist);
        
        // VERIFICATION: Immediately verify the data was stored correctly
        const retrievedData = WealthicaStorage.getConnectionData();
        if (!retrievedData?.institutionId) {
          logger.error('useWealthicaCallbackHandler', 'CRITICAL: Connection data persistence failed - institution ID missing after storage', {
            originalInstitutionId: connectionData.institutionId,
            retrievedData: retrievedData,
            shouldPersist
          });
          return { success: false, error: 'Connection data persistence failed - institution ID not stored correctly' };
        }
        
        logger.info('useWealthicaCallbackHandler', 'Connection data persistence verified successfully', {
          storedInstitutionId: retrievedData.institutionId,
          storedProviderName: retrievedData.providerName
        });
      }
      WealthicaStorage.setConnectionConfirmed(true);

      // 5. Update store connection state
      // Server-only token model: bearerToken is NOT required to connect the store.
      if (connectionData) {
        try {
          logger.info('useWealthicaCallbackHandler', 'Calling store connect()', {
            institutionId: connectionData.institutionId,
            providerName: connectionData.providerName,
            hasConnectionData: true,
            hasBearerToken: !!bearerToken
          });

          await connect(connectionData, bearerToken || '');
          logger.info('useWealthicaCallbackHandler', 'Store connect() completed successfully');

          // FLOW CLEANUP: Defer account fetch until after redirect
          // The Investments page (Wealthica2GoldContainer) will start a guarded fetch on mount.
          // Triggering a fetch here causes an in-flight request that is often aborted by the redirect,
          // producing a transient "Failed to fetch accounts" error. Deferring keeps the flow clean.
          logger.info('useWealthicaCallbackHandler', 'Deferring account fetch to post-redirect page load');

          // One-shot force: ensure the newly-added institution fetches immediately after hydration, even if
          // a recent batch fetch would otherwise cause TTL de-dupe to skip it. Use session storage so it's
          // cleared naturally and does not leak across sessions.
          try {
            WealthicaStorage.setItem('WEALTHICA2_FORCE_FETCH_INSTITUTION_ID', connectionData.institutionId, false);
            logger.debug('useWealthicaCallbackHandler', 'Scheduled post-hydration force fetch for institution', {
              institutionId: connectionData.institutionId
            });
          } catch { /* noop */ }
        } catch (connectError) {
          logger.error('useWealthicaCallbackHandler', 'Error calling store connect()', {
            error: connectError,
            connectionData,
            bearerToken: bearerToken ? `${bearerToken.substring(0, 20)}...` : null
          });
        }
      } else {
        logger.error('useWealthicaCallbackHandler', 'Missing connection data after callback - cannot connect store');
      }

      // 6. Clean up navigation state
      navigation.clearAllNavigationData();

      // 7. Set connection status to connected after successful processing
      setStatus('connected');
      logger.info('useWealthicaCallbackHandler', 'Connection status set to connected');

      // 8. Mark callback as processed to prevent reprocessing
      navigation.saveToStorage('WEALTHICA2_CALLBACK_PROCESSED', 'true');
      navigation.saveToStorage('WEALTHICA2_CALLBACK_TIME', Date.now().toString());

      // 9. Skip duplicate auto-fetch since we now do immediate fetching after connection
      logger.info('useWealthicaCallbackHandler', 'Skipping duplicate auto-fetch - accounts already fetched immediately after connection');

      logger.info('useWealthicaCallbackHandler', 'Callback processing completed successfully', {
        institutionId: connectionData.institutionId,
        providerName: connectionData.providerName,
        userId: opts.userId,
        callbackProcessed: true
      });
      
      // Clear any error states and redirect to investments
      logger.info('useWealthicaCallbackHandler', 'Setting redirect timeout for completed callback', {
        delay: 100,
        timestamp: new Date().toISOString()
      });
      
      // Call onSuccess callback to notify UI component
      if (opts.onSuccess) {
        opts.onSuccess();
      }
      
      setTimeout(() => {
        logger.info('useWealthicaCallbackHandler', 'Executing redirect for completed callback', {
          timestamp: new Date().toISOString()
        });
        navigation.redirectToInvestments();
      }, 100); // Small delay to ensure state updates are processed

      return { 
        success: true, 
        connectionData, 
        bearerToken 
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process Wealthica connection';
      
      logger.error('useWealthicaCallbackHandler', 'Callback processing failed', {
        error: error instanceof Error ? error.stack : String(error),
        userId: opts.userId,
        timestamp: new Date().toISOString()
      });
      
      // Update store with error state
      setError(errorMessage);
      setStatus('error');
      
      // Save general connection error
      try {
        const connectionInfo: Partial<WealthicaConnection> = {};
        await WealthicaConnectionService.saveConnectionError(
          opts.userId,
          connectionInfo,
          errorMessage
        );
      } catch (dbError) {
        logger.warn('useWealthicaCallbackHandler', 'Failed to save connection error status', {
          error: dbError,
          originalError: errorMessage,
          userId: opts.userId
        });
      }
      
      if (opts.onError) {
        opts.onError(errorMessage);
      }
      
      return { success: false, error: errorMessage };
    }
  }, [navigation, connect, setError, setStatus, queryClient, multiStore]);

  return { processCallback };
}
